import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, UserPlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AdminFoundersKeys,
  assignMentor,
  AssignMentorDto,
} from "@/features/founders/api/admin-founders";
import { getMentors, MentorsKeys } from "@/features/mentors/api/mentors";
import { useToast } from "@/hooks/use-toast";

interface AssignMentorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  licenseeId: string | null;
  licenseeName: string;
  currentMentorId?: string | null;
}

const AssignMentorDialog: React.FC<AssignMentorDialogProps> = ({
  open,
  onOpenChange,
  licenseeId,
  licenseeName,
  currentMentorId = null,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [mentorId, setMentorId] = React.useState<string>("");

  const { data: mentors = [], isLoading } = useQuery({
    queryKey: [MentorsKeys.GET_MENTORS],
    queryFn: getMentors,
    enabled: open,
  });

  React.useEffect(() => {
    if (open) {
      setMentorId(currentMentorId || "");
    }
  }, [open, currentMentorId]);

  const { mutate: assign, isPending } = useMutation({
    mutationFn: (payload: AssignMentorDto) => assignMentor(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [AdminFoundersKeys.GET_FOUNDERS],
      });
      if (licenseeId) {
        queryClient.invalidateQueries({
          queryKey: [AdminFoundersKeys.GET_FOUNDER, licenseeId],
        });
      }
      toast({ title: "Mentor assigned successfully" });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to assign mentor",
        description: error?.response?.data?.message || "",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    if (!licenseeId || !mentorId) return;
    assign({ licenseeId, mentorId });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Assign Mentor
          </DialogTitle>
          <DialogDescription>
            Choose a mentor for {licenseeName}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label className="text-sm font-medium text-muted-foreground">
                Mentor
              </label>
              <Select value={mentorId} onValueChange={setMentorId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a mentor" />
                </SelectTrigger>
                <SelectContent>
                  {mentors.map((mentor) => (
                    <SelectItem key={mentor._id} value={mentor._id}>
                      {`${mentor.firstName} ${mentor.lastName}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || !mentorId || isPending}
            className="gap-2"
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignMentorDialog;
